import { useState, useEffect, useCallback } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import api from "../../lib/api";

import { Button } from "../../components/ui/Button";

import { Input } from "../../components/ui/Input";

import {

  ModalidadConfigTable,

  type ModalidadConfigRow,

} from "../../components/ModalidadConfigTable";



interface ModalidadRow {

  id: number;

  nombre: string;

  tipo: string;

  precio_venta: number | null;

  precio_minimo: number;

}



interface PaqueteModalidad {

  modalidadId: number;

  nombre: string;

  tipo: string;

  precioVenta: number | null;

}



function rowsToPackageConfig(rows: ModalidadRow[]): ModalidadConfigRow[] {

  return rows.map((r) => ({

    id: r.id,

    nombre: r.nombre,

    tipo: r.tipo,

    precio_minimo: r.precio_minimo,

    activo: true,

    precioVentaInput: r.precio_venta != null ? String(r.precio_venta) : "",

  }));

}



function rowsToPackageConfigForEdit(

  catalogRows: ModalidadRow[],

  paqueteRows: PaqueteModalidad[]

): ModalidadConfigRow[] {

  const paqueteIds = new Set(paqueteRows.map((p) => p.modalidadId));

  const precioById = new Map(paqueteRows.map((p) => [p.modalidadId, p.precioVenta]));



  return catalogRows.map((r) => {

    const inPaquete = paqueteIds.has(r.id);

    const precioVenta = precioById.get(r.id);

    return {

      id: r.id,

      nombre: r.nombre,

      tipo: r.tipo,

      precio_minimo: r.precio_minimo,

      activo: inPaquete,

      precioVentaInput: inPaquete && precioVenta != null ? String(precioVenta) : "",

    };

  });

}



export function NuevoSeguroPage() {

  const navigate = useNavigate();

  const { id: editIdParam } = useParams<{ id: string }>();

  const editId = editIdParam ? parseInt(editIdParam, 10) : NaN;

  const isEditMode = !Number.isNaN(editId);



  const [rows, setRows] = useState<ModalidadConfigRow[]>([]);

  const [loading, setLoading] = useState(true);

  const [eventoRef, setEventoRef] = useState("");

  const [enviarCertificadoEmail, setEnviarCertificadoEmail] = useState(true);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");



  const fetchData = useCallback(async () => {

    setLoading(true);

    setError("");

    try {

      const catalogRes = await api.get<ModalidadRow[]>("/cliente/modalidades");

      const catalog = Array.isArray(catalogRes.data) ? catalogRes.data : [];



      if (isEditMode) {

        const seguroRes = await api.get<{

          eventoReferencia?: string | null;

          enviarCertificadoEmail?: boolean;

          modalidades_paquete?: PaqueteModalidad[];

        }>(`/cliente/seguros/${editId}`);

        setEventoRef(seguroRes.data.eventoReferencia?.trim() ?? "");

        setEnviarCertificadoEmail(seguroRes.data.enviarCertificadoEmail !== false);

        setRows(rowsToPackageConfigForEdit(catalog, seguroRes.data.modalidades_paquete ?? []));

      } else {

        setRows(rowsToPackageConfig(catalog));

      }

    } catch {

      setRows([]);

      if (isEditMode) {

        setError("No se pudo cargar el paquete");

      }

    } finally {

      setLoading(false);

    }

  }, [editId, isEditMode]);



  useEffect(() => {

    void fetchData();

  }, [fetchData]);



  const handleChange = (id: number, field: "activo" | "precioVenta", value: boolean | string) => {

    setError("");

    setRows((prev) =>

      prev.map((r) => {

        if (r.id !== id) return r;

        if (field === "activo") return { ...r, activo: value as boolean };

        return { ...r, precioVentaInput: value as string };

      })

    );

  };



  const validateAndBuildPayload = () => {

    const marcadas = rows.filter((r) => r.activo);

    if (marcadas.length === 0) {

      setError("Selecciona al menos una modalidad");

      return null;

    }



    for (const row of marcadas) {

      const trimmed = row.precioVentaInput.trim();

      if (!trimmed) continue;

      const n = parseFloat(trimmed);

      if (Number.isNaN(n) || n < row.precio_minimo) {

        setError(

          `El precio de "${row.nombre}" no puede ser inferior a ${row.precio_minimo.toFixed(2)} €`

        );

        return null;

      }

    }



    const modalidades = marcadas.map((row) => {

      const trimmed = row.precioVentaInput.trim();

      return {

        modalidad_id: row.id,

        precio_venta: trimmed ? parseFloat(trimmed) : null,

      };

    });



    return {

      evento_referencia: eventoRef.trim() || undefined,

      modalidades,

      enviar_certificado_email: enviarCertificadoEmail,

    };

  };



  const handleSave = async () => {

    const payload = validateAndBuildPayload();

    if (!payload) return;



    setError("");

    setSaving(true);

    try {

      if (isEditMode) {

        await api.patch(`/cliente/seguros/${editId}`, payload);

        navigate("/dashboard/seguros", {

          state: { openSeguroId: editId, tab: "resumen" },

        });

      } else {

        const res = await api.post<{ id: number }>("/cliente/seguros", payload);

        navigate("/dashboard/pilotos", {
          state: { seguroId: res.data.id },
        });

      }

    } catch (err: unknown) {

      setError(

        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??

          (isEditMode ? "Error al guardar el paquete" : "Error al crear el paquete")

      );

    } finally {

      setSaving(false);

    }

  };



  return (

    <div className="space-y-6">

      <div className="flex flex-wrap items-start justify-between gap-4">

        <div className="space-y-2">

          <h1 className="text-2xl font-semibold text-[#2C2C2C]">

            {isEditMode ? "Editar paquete" : "Crear paquete de seguros"}

          </h1>

          <p className="max-w-2xl text-sm text-gray-600">

            {isEditMode

              ? "Modifica el nombre del paquete, las modalidades incluidas y sus precios de venta."

              : "Selecciona las modalidades que incluye este paquete y establece el precio de venta para cada una."}

          </p>

        </div>

        <Button variant="ghost" onClick={() => navigate("/dashboard/seguros")}>

          Volver

        </Button>

      </div>



      {loading ? (

        <div className="py-12 text-center text-gray-500">Cargando…</div>

      ) : rows.length === 0 ? (

        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600 space-y-3">

          <p>

            {isEditMode && error

              ? error

              : "No tienes modalidades asignadas. Contacta con el administrador o configura tu oferta en Mis modalidades."}

          </p>

          {!isEditMode && (

            <Link

              to="/dashboard/modalidades"

              className="inline-block text-sm font-medium text-[#1A3AB8] hover:underline"

            >

              Ir a Mis modalidades

            </Link>

          )}

        </div>

      ) : (

        <>

          <Input

            label="Paquete (opcional)"

            placeholder="Nombre del paquete o referencia..."

            value={eventoRef}

            onChange={(e) => setEventoRef(e.target.value)}

          />



          <label className="flex items-start gap-3 cursor-pointer">

            <input

              type="checkbox"

              className="mt-1 h-4 w-4 rounded border-gray-300 text-[#1A3AB8] focus:ring-[#1A3AB8]"

              checked={enviarCertificadoEmail}

              onChange={(e) => setEnviarCertificadoEmail(e.target.checked)}

            />

            <span className="text-sm text-gray-700">

              Enviar certificado por email al piloto

            </span>

          </label>



          <ModalidadConfigTable

            modalidades={rows}

            onChange={handleChange}

            checkLabel="Incluir en paquete"

          />



          {error && <p className="text-sm text-red-600">{error}</p>}



          <div className="flex gap-2">

            <Button variant="secondary" onClick={() => navigate("/dashboard/seguros")}>

              Cancelar

            </Button>

            <Button loading={saving} onClick={handleSave}>

              {isEditMode ? "Guardar cambios" : "Crear paquete"}

            </Button>

          </div>

        </>

      )}

    </div>

  );

}


